import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { codeMapApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { ScreenMetaInput, ScreenNode } from '../types/code-map.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business API レスポンスの画面ツリーを正規化する。旧 API が statuses を返さないケースに対応し、undefined を空配列に埋める。
 * @graph-connects none
 */
export function normalizeTree(nodes: ScreenNode[]): ScreenNode[] {
  return nodes.map((node) => ({
    ...node,
    statuses: node.statuses ?? [],
    children: normalizeTree(node.children),
  }));
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 画面マップ（最新スナップショットの画面ツリー＋人手メタ）を取得するフック。select で API レスポンスを正規化し、statuses が常に配列であることを保証する。
 * @graph-connects codeMapApi [calls] 画面マップ API クライアント
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useScreens() {
  return useQuery({
    queryKey: queryKeys.codeMap.screens,
    queryFn: codeMapApi.getScreens,
    select: (data) => ({
      ...data,
      tree: normalizeTree(data.tree),
    }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 画面メタ（名前/説明/カテゴリ）を保存するミューテーション。成功時に画面ツリーを invalidate して再取得する。
 * @graph-connects codeMapApi [calls] 画面メタ保存
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useSaveScreenMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ routePath, input }: { routePath: string; input: ScreenMetaInput }) =>
      codeMapApi.saveMeta(routePath, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.codeMap.screens });
    },
  });
}
